import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../supabase';
import { createClient } from '@supabase/supabase-js';
import Navbar from '../components/Navbar';
import { toast } from 'sonner';
import {
  LayoutDashboard, Building2, Users as UsersIcon, LogOut, Plus,
  Edit2, Trash2, CheckCircle2, Copy, Check, Menu, X, Building,
  Stethoscope, Pill, Beaker, User
} from 'lucide-react';
import './AdminDashboard.css';

// Transient Auth Client
function getTransientAuthClient() {
  return createClient(
    process.env.REACT_APP_SUPABASE_URL,
    process.env.REACT_APP_SUPABASE_ANON_KEY,
    { auth: { persistSession: false, autoRefreshToken: false } }
  );
}

function AdminDashboard() {
  const navigate = useNavigate();
  const { logout, currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [clinics, setClinics] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedClinicFilter, setSelectedClinicFilter] = useState('all');

  // Confirmation Modal for Deletions
  const [confirmDelete, setConfirmDelete] = useState({ isOpen: false, type: null, id: null });

  // Credentials Modal state
  const [credsModal, setCredsModal] = useState(null); // { email, password }
  const [copiedField, setCopiedField] = useState('');

  // Form states
  const [clinicForm, setClinicForm] = useState({
    name: '', address: '', phone: '', has_pharmacy: true, has_lab: true,
    print_header_footer: true, theme_color: '#f8fafc',
    show_clinic_phone: true, show_doctor_phone: true,
    show_working_hours: true, signature_type: 'digital',
    logo_url: ''
  });
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const [userForm, setUserForm] = useState({
    name: '', email: '', phone: '', role: 'doctor', clinic_id: '',
    password: '', active: true, qualification: '', specialization: '',
    registration_number: '', working_hours: '', available_days: []
  });

  useEffect(() => {
    fetchClinics();
    fetchUsers();
  }, []);

  async function fetchClinics() {
    try {
      const { data, error } = await supabase.from('clinics').select('*').order('created_at', { ascending: false });
      if (error) throw error;
      setClinics(data || []);
    } catch (error) {
      toast.error('Failed to load clinics');
    }
  }

  async function handleLogoUpload(e) {
    try {
      if (!e.target.files || e.target.files.length === 0) return;
      setUploadingLogo(true);
      const file = e.target.files[0];
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2)}_${Date.now()}.${fileExt}`;
      const filePath = `logos/${fileName}`;

      const { error: uploadError } = await supabase.storage.from('clinic-assets').upload(filePath, file);
      if (uploadError) throw uploadError;

      const { data } = supabase.storage.from('clinic-assets').getPublicUrl(filePath);
      setClinicForm({ ...clinicForm, logo_url: data.publicUrl });
      toast.success('Logo uploaded');
    } catch (error) {
      toast.error('Error uploading logo: ' + error.message);
    } finally {
      setUploadingLogo(false);
    }
  }

  async function fetchUsers() {
    try {
      const { data, error } = await supabase.from('users').select('*').neq('role', 'admin').order('created_at', { ascending: false });
      if (error) throw error;
      setUsers(data || []);
    } catch (error) {
      toast.error('Failed to load users');
    }
  }

  async function handleCreateClinic(e) {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingItem) {
        const { error } = await supabase.from('clinics').update({ ...clinicForm, updated_at: new Date().toISOString() }).eq('id', editingItem.id);
        if (error) throw error;
        toast.success('Clinic updated successfully');
      } else {
        const { error } = await supabase.from('clinics').insert({ ...clinicForm, active: true });
        if (error) throw error;
        toast.success('Clinic created successfully');
      }
      setShowModal(false);
      setClinicForm({
        name: '', address: '', phone: '', has_pharmacy: true, has_lab: true,
        print_header_footer: true, theme_color: '#f8fafc', show_clinic_phone: true,
        show_doctor_phone: true, show_working_hours: true, signature_type: 'digital', logo_url: ''
      });
      setEditingItem(null);
      fetchClinics();
    } catch (error) {
      toast.error(error.message || 'Failed to save clinic');
    }
    setLoading(false);
  }

  async function handleCreateUser(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const userData = { ...userForm };

      // Clean up fields based on role
      if (userData.role !== 'doctor') {
        delete userData.qualification;
        delete userData.specialization;
        delete userData.registration_number;
        delete userData.working_hours;
        delete userData.available_days;
      }

      // Validate clinic capabilities
      if (userData.role === 'pharmacy' || userData.role === 'lab') {
        const selectedClinic = clinics.find(c => c.id === userData.clinic_id);
        if (selectedClinic) {
          if (userData.role === 'pharmacy' && !selectedClinic.has_pharmacy) {
            toast.error('Cannot add Pharmacy staff: The selected clinic does not have the Pharmacy module enabled.');
            setLoading(false);
            return;
          }
          if (userData.role === 'lab' && !selectedClinic.has_lab) {
            toast.error('Cannot add Lab staff: The selected clinic does not have the Lab module enabled.');
            setLoading(false);
            return;
          }
        }
      }

      if (editingItem) {
        const { password, email, ...updateData } = userData;
        const { error } = await supabase.from('users').update({ ...updateData, updated_at: new Date().toISOString() }).eq('id', editingItem.id);
        if (error) throw error;
        toast.success('User profile updated successfully');
        setShowModal(false);
        resetUserForm();
        setEditingItem(null);
        fetchUsers();
      } else {
        const transientClient = getTransientAuthClient();
        const { data: authData, error: authError } = await transientClient.auth.signUp({
          email: userData.email, password: userData.password,
        });

        if (authError) {
          if (authError.message.includes('already registered')) {
            toast.error('Email is already registered. Use a different email.');
          } else {
            throw authError;
          }
          setLoading(false);
          return;
        }

        const newUserId = authData.user.id;
        const savedPassword = userData.password;
        const { password, ...profileData } = userData;

        const { error: profileError } = await supabase.from('users').insert({ id: newUserId, ...profileData });
        if (profileError) throw profileError;

        await transientClient.auth.signOut();

        toast.success('User created successfully');
        setShowModal(false);
        resetUserForm();
        setEditingItem(null);
        setCredsModal({ email: userData.email, password: savedPassword });
        fetchUsers();
      }
    } catch (error) {
      toast.error(error.message || 'Failed to save user');
    }
    setLoading(false);
  }

  async function toggleClinicActive(clinicId, currentStatus) {
    try {
      const { error } = await supabase.from('clinics').update({ active: !currentStatus, updated_at: new Date().toISOString() }).eq('id', clinicId);
      if (error) throw error;
      toast.success(`Clinic status updated to ${!currentStatus ? 'Active' : 'Inactive'}`);
      fetchClinics();
    } catch (error) {
      toast.error('Failed to update clinic status');
    }
  }

  async function toggleUserActive(userId, currentStatus) {
    try {
      const { error } = await supabase.from('users').update({ active: !currentStatus, updated_at: new Date().toISOString() }).eq('id', userId);
      if (error) throw error;
      toast.success(`User status updated to ${!currentStatus ? 'Active' : 'Inactive'}`);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update user status');
    }
  }

  function triggerDelete(type, id) {
    setConfirmDelete({ isOpen: true, type, id });
  }

  async function executeDelete() {
    const { type, id } = confirmDelete;
    setConfirmDelete({ isOpen: false, type: null, id: null });
    try {
      if (type === 'clinic') {
        const { error } = await supabase.from('clinics').delete().eq('id', id);
        if (error) throw error;
        toast.success('Clinic deleted successfully');
        fetchClinics();
      } else {
        const { error } = await supabase.from('users').delete().eq('id', id);
        if (error) throw error;
        toast.success('User deleted successfully');
        fetchUsers();
      }
    } catch (error) {
      toast.error(`Error deleting: ${error.message}`);
    }
  }

  function resetUserForm() {
    setUserForm({
      name: '', email: '', phone: '', role: 'doctor', clinic_id: '',
      password: '', active: true, qualification: '', specialization: '',
      registration_number: '', working_hours: '', available_days: []
    });
  }

  function openModal(type, item = null) {
    setModalType(type);
    setEditingItem(item);
    if (type === 'clinic') {
      setClinicForm(item ? {
        name: item.name, address: item.address || '', phone: item.phone || '', has_pharmacy: item.has_pharmacy ?? true, has_lab: item.has_lab ?? true,
        print_header_footer: item.print_header_footer ?? true, theme_color: item.theme_color || '#f8fafc',
        show_clinic_phone: item.show_clinic_phone ?? true, show_doctor_phone: item.show_doctor_phone ?? true,
        show_working_hours: item.show_working_hours ?? true, signature_type: item.signature_type || 'digital',
        logo_url: item.logo_url || ''
      } : {
        name: '', address: '', phone: '', has_pharmacy: true, has_lab: true,
        print_header_footer: true, theme_color: '#f8fafc',
        show_clinic_phone: true, show_doctor_phone: true,
        show_working_hours: true, signature_type: 'digital',
        logo_url: ''
      });
    } else {
      if (item) {
        setUserForm({ ...item, password: '', available_days: item.available_days || [] });
      } else {
        resetUserForm();
      }
    }
    setShowModal(true);
  }

  async function handleLogout() {
    await logout();
    navigate('/');
  }

  function handleDayToggle(day) {
    const days = userForm.available_days || [];
    if (days.includes(day)) {
      setUserForm({ ...userForm, available_days: days.filter(d => d !== day) });
    } else {
      setUserForm({ ...userForm, available_days: [...days, day] });
    }
  }

  async function copyToClipboard(text, field) {
    await navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedField(''), 2000);
  }

  const filteredUsers = selectedClinicFilter === 'all'
    ? users
    : users.filter(u => u.clinic_id === selectedClinicFilter);

  const stats = {
    totalClinics: clinics.length,
    totalStaff: users.filter(u => ['doctor', 'pharmacy', 'lab', 'reception'].includes(u.role)).length,
    totalPatients: users.filter(u => u.role === 'patient').length,
    totalDoctors: users.filter(u => u.role === 'doctor').length
  };

  return (
    <>
      <Navbar />
      <div className="admin-layout">
        {/* Mobile Menu Button */}
        <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>

        {mobileMenuOpen && (
          <div className="mobile-overlay" onClick={() => setMobileMenuOpen(false)}></div>
        )}

        <aside className={`admin-sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
          <nav className="sidebar-nav">
            <button className={`nav-item ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => { setActiveTab('overview'); setMobileMenuOpen(false); }}>
              <LayoutDashboard size={20} />
              <span>Overview</span>
            </button>
            <button className={`nav-item ${activeTab === 'clinics' ? 'active' : ''}`} onClick={() => { setActiveTab('clinics'); setMobileMenuOpen(false); }}>
              <Building2 size={20} />
              <span>Clinics</span>
            </button>
            <button className={`nav-item ${activeTab === 'users' ? 'active' : ''}`} onClick={() => { setActiveTab('users'); setMobileMenuOpen(false); }}>
              <UsersIcon size={20} />
              <span>Staff & Patients</span>
            </button>
          </nav>
          <div className="sidebar-footer">
            <button className="logout-btn-sidebar" onClick={handleLogout}>
              <LogOut size={18} />
              <span>Logout</span>
            </button>
          </div>
        </aside>

        <main className="admin-main">
          <header className="admin-header">
            <div>
              <h1>
                {activeTab === 'overview' && 'Dashboard Overview'}
                {activeTab === 'clinics' && 'Manage Clinics'}
                {activeTab === 'users' && 'Manage Staff & Patients'}
              </h1>
              <p className="header-subtitle">{currentUser?.email}</p>
            </div>
          </header>

          <div className="admin-content">
            {activeTab === 'overview' && (
              <>
                <div className="stats-grid">
                  <div className="stat-card">
                    <div className="stat-icon blue"><Building2 size={28} /></div>
                    <div className="stat-info">
                      <span className="stat-label">Total Clinics</span>
                      <span className="stat-value">{stats.totalClinics}</span>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-icon green"><UsersIcon size={28} /></div>
                    <div className="stat-info">
                      <span className="stat-label">Total Staff</span>
                      <span className="stat-value">{stats.totalStaff}</span>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-icon purple"><Stethoscope size={28} /></div>
                    <div className="stat-info">
                      <span className="stat-label">Doctors</span>
                      <span className="stat-value">{stats.totalDoctors}</span>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-icon orange"><User size={28} /></div>
                    <div className="stat-info">
                      <span className="stat-label">Patients</span>
                      <span className="stat-value">{stats.totalPatients}</span>
                    </div>
                  </div>
                </div>

                <div className="quick-actions-card">
                  <h2>Quick Actions</h2>
                  <div className="quick-actions">
                    <button className="action-btn" onClick={() => openModal('clinic')}>
                      <Building2 size={20} className="text-primary-600" />
                      Create New Clinic
                    </button>
                    <button className="action-btn" onClick={() => openModal('user')}>
                      <User size={20} className="text-primary-600" />
                      Add Staff Member
                    </button>
                  </div>
                </div>
              </>
            )}

            {activeTab === 'clinics' && (
              <div className="content-card">
                <div className="card-header">
                  <h2>Clinics ({clinics.length})</h2>
                  <button className="btn-primary" onClick={() => openModal('clinic')}>
                    <Plus size={18} />
                    Add Clinic
                  </button>
                </div>
                {clinics.length === 0 ? (
                  <div className="empty-state">
                    <Building2 size={64} className="text-gray-300" />
                    <h3>No clinics yet</h3>
                    <p>Create your first clinic to get started</p>
                  </div>
                ) : (
                  <div className="table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Clinic Name</th><th>Address</th><th>Phone</th><th>Modules</th><th>Status</th><th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clinics.map(clinic => (
                          <tr key={clinic.id}>
                            <td><strong>{clinic.name}</strong></td>
                            <td>{clinic.address || '-'}</td>
                            <td>{clinic.phone || '-'}</td>
                            <td>
                              <div style={{ display: 'flex', gap: '0.5rem' }}>
                                {clinic.has_pharmacy && <span className="badge badge-pharmacy" style={{ fontSize: '0.7rem', padding: '0.2rem 0.5rem' }}>Pharmacy</span>}
                                {clinic.has_lab && <span className="badge badge-lab" style={{ fontSize: '0.7rem', padding: '0.2rem 0.5rem' }}>Lab</span>}
                                {!clinic.has_pharmacy && !clinic.has_lab && <span style={{ color: 'var(--gray-500)', fontSize: '0.875rem' }}>Basic</span>}
                              </div>
                            </td>
                            <td className={clinic.active === false ? 'inactive-row' : ''}>
                              <button className={`status-toggle ${clinic.active !== false ? 'active' : 'inactive'}`} onClick={() => toggleClinicActive(clinic.id, clinic.active !== false)}>
                                {clinic.active !== false ? 'Active' : 'Inactive'}
                              </button>
                            </td>
                            <td>
                              <button className="btn-icon" onClick={() => openModal('clinic', clinic)} title="Edit">
                                <Edit2 size={18} />
                              </button>
                              <button className="btn-icon danger" onClick={() => triggerDelete('clinic', clinic.id)} title="Delete">
                                <Trash2 size={18} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'users' && (
              <div className="content-card">
                <div className="card-header">
                  <div className="header-left-section">
                    <h2>Staff & Patients ({filteredUsers.length})</h2>
                    <div className="filter-group">
                      <label htmlFor="clinic-filter">Filter by Clinic:</label>
                      <select id="clinic-filter" value={selectedClinicFilter} onChange={(e) => setSelectedClinicFilter(e.target.value)} className="clinic-filter-select form-input">
                        <option value="all">All Clinics</option>
                        {clinics.map(clinic => (
                          <option key={clinic.id} value={clinic.id}>{clinic.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <button className="btn-primary" onClick={() => openModal('user')}>
                    <Plus size={18} />
                    Add User
                  </button>
                </div>
                {filteredUsers.length === 0 ? (
                  <div className="empty-state">
                    <UsersIcon size={64} />
                    <h3>No users in this clinic</h3>
                    <p>Add staff members and patients</p>
                  </div>
                ) : (
                  <div className="table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Name</th><th>Email</th><th>Phone</th><th>Role</th><th>Clinic</th><th>Status</th><th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredUsers.map(user => (
                          <tr key={user.id} className={user.active === false ? 'inactive-row' : ''}>
                            <td><strong>{user.name}</strong></td>
                            <td>{user.email || '-'}</td>
                            <td>{user.phone || '-'}</td>
                            <td><span className={`badge badge-${user.role}`}>{user.role}</span></td>
                            <td>{clinics.find(c => c.id === user.clinic_id)?.name || '-'}</td>
                            <td>
                              <button className={`status-toggle ${user.active !== false ? 'active' : 'inactive'}`} onClick={() => toggleUserActive(user.id, user.active !== false)}>
                                {user.active !== false ? 'Active' : 'Inactive'}
                              </button>
                            </td>
                            <td>
                              <button className="btn-icon" onClick={() => openModal('user', user)} title="Edit">
                                <Edit2 size={18} />
                              </button>
                              <button className="btn-icon danger" onClick={() => triggerDelete('user', user.id)} title="Delete">
                                <Trash2 size={18} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingItem ? 'Edit' : 'Create'} {modalType === 'clinic' ? 'Clinic' : 'User'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}><X size={24} /></button>
            </div>

            {modalType === 'clinic' ? (
              <form onSubmit={handleCreateClinic}>
                <div className="form-group">
                  <label>Clinic Name *</label>
                  <input type="text" className="form-input" value={clinicForm.name} onChange={(e) => setClinicForm({ ...clinicForm, name: e.target.value })} required placeholder="e.g., Downtown Medical Center" />
                </div>
                <div className="form-group">
                  <label>Address</label>
                  <input type="text" className="form-input" value={clinicForm.address} onChange={(e) => setClinicForm({ ...clinicForm, address: e.target.value })} placeholder="Full address" />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input type="tel" className="form-input" value={clinicForm.phone} onChange={(e) => setClinicForm({ ...clinicForm, phone: e.target.value })} placeholder="Contact number" />
                </div>
                <div className="form-group" style={{ marginTop: '1.5rem', marginBottom: '1.5rem' }}>
                  <label style={{ marginBottom: '0.75rem', display: 'block' }}>Clinic Modules</label>
                  <div style={{ display: 'flex', flexDirection: 'column', background: 'var(--gray-50)', padding: '0 1rem', borderRadius: 'var(--radius-md)', border: '1px solid var(--gray-200)' }}>
                    {[
                      { key: 'has_pharmacy', label: 'In-House Pharmacy', desc: 'Enable the pharmacy management module for this clinic' },
                      { key: 'has_lab', label: 'In-House Lab', desc: 'Enable the diagnostic lab module for this clinic' },
                    ].map(({ key, label, desc }, i, arr) => (
                      <label key={key} style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 0', borderBottom: i < arr.length - 1 ? '1px solid var(--gray-100)' : 'none', cursor: 'pointer', gap: '1.5rem' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ fontSize: '0.95rem', color: 'var(--gray-800)', fontWeight: 500 }}>{label}</span>
                          <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: '0.2rem', display: 'block' }}>{desc}</span>
                        </div>
                        <div className="toggle-switch" style={{ flexShrink: 0 }}>
                          <input type="checkbox" checked={clinicForm[key]} onChange={(e) => setClinicForm({ ...clinicForm, [key]: e.target.checked })} />
                          <span className="toggle-slider"></span>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                <div style={{ marginTop: '2rem', paddingTop: '1rem', borderTop: '1px solid var(--gray-200)' }}>
                  <h3 style={{ fontSize: '1rem', marginBottom: '1rem' }}>Prescription Settings</h3>

                  <div className="form-row">
                    <div className="form-group" style={{ flex: 1 }}>
                      <label>Clinic Logo (Header)</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '0.25rem' }}>
                        {clinicForm.logo_url && !uploadingLogo ? (
                          <div style={{ position: 'relative', display: 'inline-block' }}>
                            <img src={clinicForm.logo_url} alt="Logo" style={{ height: '50px', borderRadius: '4px', border: '1px solid var(--gray-200)', padding: '4px', background: 'white' }} />
                            <button
                              type="button"
                              onClick={() => setClinicForm({ ...clinicForm, logo_url: '' })}
                              style={{ position: 'absolute', top: '-8px', right: '-8px', background: 'white', color: 'var(--danger-600)', border: '1px solid var(--gray-200)', borderRadius: '50%', width: '22px', height: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: 'var(--shadow-sm)' }}
                              title="Remove Logo"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ) : (
                          <input type="file" accept="image/*" onChange={handleLogoUpload} disabled={uploadingLogo} className="form-input" style={{ flex: 1 }} />
                        )}
                        {uploadingLogo && <span style={{ fontSize: '0.8rem', color: 'var(--primary-600)' }}>Uploading...</span>}
                      </div>

                      <div style={{ marginTop: '1.5rem' }}>
                        <label>Signature Type</label>
                        <select className="form-input" value={clinicForm.signature_type} onChange={(e) => setClinicForm({ ...clinicForm, signature_type: e.target.value })}>
                          <option value="digital">Digital Signature</option>
                          <option value="none">No Signature (Text Only)</option>
                        </select>
                      </div>
                    </div>

                    <div className="form-group" style={{ flex: 1 }}>
                      <label>Theme Color (Header & Footer Tint)</label>
                      <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginBottom: '0.5rem', marginTop: 0 }}>Select a light background tint for the prescription paper.</p>
                      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                        {[
                          { color: '#ffffff', name: 'White' },
                          { color: '#f8fafc', name: 'Slate' },
                          { color: '#f0fdf4', name: 'Green' },
                          { color: '#eff6ff', name: 'Blue' },
                          { color: '#faf5ff', name: 'Purple' }
                        ].map(preset => (
                          <button
                            key={preset.color}
                            type="button"
                            onClick={() => setClinicForm({ ...clinicForm, theme_color: preset.color })}
                            style={{
                              width: '36px', height: '36px', borderRadius: '50%', cursor: 'pointer',
                              backgroundColor: preset.color,
                              border: clinicForm.theme_color === preset.color ? '2px solid var(--primary-600)' : '1px solid var(--gray-300)',
                              boxShadow: clinicForm.theme_color === preset.color ? '0 0 0 2px white inset, var(--shadow-sm)' : 'none',
                              outline: 'none', transition: 'all 0.2s'
                            }}
                            title={preset.name}
                          />
                        ))}
                      </div>
                    </div>
                  </div>

                  <div style={{ background: 'var(--gray-50)', padding: '0 1rem', borderRadius: 'var(--radius-md)', border: '1px solid var(--gray-200)' }}>
                    {[
                      { key: 'print_header_footer', label: 'Include Header & Footer in PDF/Print', desc: 'If disabled, prints on your pre-printed letterhead', bold: true },
                      { key: 'show_clinic_phone', label: 'Show Clinic Phone', desc: null },
                      { key: 'show_doctor_phone', label: "Show Doctor's Phone", desc: null },
                      { key: 'show_working_hours', label: 'Show Working Hours', desc: null },
                    ].map(({ key, label, desc, bold }, i, arr) => (
                      <label key={key} style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 0', borderBottom: i < arr.length - 1 ? '1px solid var(--gray-100)' : 'none', cursor: 'pointer', gap: '1.5rem' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ fontSize: '0.95rem', color: 'var(--gray-800)', fontWeight: bold ? 600 : 500 }}>{label}</span>
                          {desc && <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: '0.2rem', display: 'block' }}>{desc}</span>}
                        </div>
                        <div className="toggle-switch" style={{ flexShrink: 0 }}>
                          <input type="checkbox" checked={clinicForm[key]} onChange={(e) => setClinicForm({ ...clinicForm, [key]: e.target.checked })} />
                          <span className="toggle-slider"></span>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="modal-actions">
                  <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                  <button type="submit" className="btn-primary" disabled={loading}>{loading ? 'Saving...' : (editingItem ? 'Update' : 'Create')}</button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleCreateUser}>
                <div className="form-group">
                  <label>Name *</label>
                  <input type="text" className="form-input" value={userForm.name} onChange={(e) => setUserForm({ ...userForm, name: e.target.value })} required placeholder="Full name" />
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <input type="email" className="form-input" value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} required placeholder="email@example.com" disabled={!!editingItem} />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input type="tel" className="form-input" value={userForm.phone} onChange={(e) => setUserForm({ ...userForm, phone: e.target.value })} placeholder="Phone number" />
                </div>
                <div className="form-group">
                  <label>Role *</label>
                  <select className="form-input" value={userForm.role} onChange={(e) => setUserForm({ ...userForm, role: e.target.value })} required>
                    <option value="doctor">Doctor</option>
                    <option value="reception">Reception</option>
                    <option value="pharmacy">Pharmacy</option>
                    <option value="lab">Lab</option>
                    <option value="patient">Patient</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Clinic *</label>
                  <select className="form-input" value={userForm.clinic_id} onChange={(e) => setUserForm({ ...userForm, clinic_id: e.target.value })} required>
                    <option value="">Select Clinic</option>
                    {clinics.map(clinic => (
                      <option key={clinic.id} value={clinic.id}>{clinic.name}</option>
                    ))}
                  </select>
                </div>

                {userForm.role === 'doctor' && (
                  <>
                    <div className="form-group">
                      <label>Qualification</label>
                      <input type="text" className="form-input" value={userForm.qualification} onChange={(e) => setUserForm({ ...userForm, qualification: e.target.value })} placeholder="e.g., MBBS, MD" />
                    </div>
                    <div className="form-group">
                      <label>Specialization</label>
                      <input type="text" className="form-input" value={userForm.specialization} onChange={(e) => setUserForm({ ...userForm, specialization: e.target.value })} placeholder="e.g., Cardiology, Pediatrics" />
                    </div>
                    <div className="form-group">
                      <label>Registration Number</label>
                      <input type="text" className="form-input" value={userForm.registration_number} onChange={(e) => setUserForm({ ...userForm, registration_number: e.target.value })} placeholder="Medical registration number" />
                    </div>
                    <div className="form-group">
                      <label>Working Hours</label>
                      <input type="text" className="form-input" value={userForm.working_hours} onChange={(e) => setUserForm({ ...userForm, working_hours: e.target.value })} placeholder="e.g., 9:00 AM - 5:00 PM" />
                    </div>
                    <div className="form-group">
                      <label>Available Days</label>
                      <div className="days-selector">
                        {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => (
                          <label key={day} className="day-checkbox">
                            <input type="checkbox" checked={(userForm.available_days || []).includes(day)} onChange={() => handleDayToggle(day)} />
                            <span>{day.slice(0, 3)}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                {!editingItem && (
                  <div className="form-group">
                    <label>Temporary Password *</label>
                    <input type="password" className="form-input" value={userForm.password} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })} required placeholder="Min. 6 characters" minLength={6} />
                  </div>
                )}

                <div className="modal-actions">
                  <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                  <button type="submit" className="btn-primary" disabled={loading}>{loading ? 'Saving...' : (editingItem ? 'Update' : 'Create')}</button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {confirmDelete.isOpen && (
        <div className="modal-overlay" onClick={() => setConfirmDelete({ isOpen: false, type: null, id: null })}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <div className="modal-header">
              <h2>Confirm Deletion</h2>
              <button className="modal-close" onClick={() => setConfirmDelete({ isOpen: false, type: null, id: null })}><X size={24} /></button>
            </div>
            <div style={{ padding: '2rem' }}>
              <p style={{ margin: 0, color: 'var(--gray-600)', lineHeight: 1.5 }}>
                Are you sure you want to delete this {confirmDelete.type}? This action cannot be undone.
              </p>
            </div>
            <div className="modal-actions" style={{ margin: 0, padding: '1rem 2rem 1.5rem', borderTop: 'none', paddingTop: 0 }}>
              <button className="btn-secondary" onClick={() => setConfirmDelete({ isOpen: false, type: null, id: null })}>Cancel</button>
              <button className="btn-danger" onClick={executeDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Credentials Modal */}
      {credsModal && (
        <div className="modal-overlay" onClick={() => setCredsModal(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '450px' }}>
            <div className="modal-header" style={{ borderBottom: 'none' }}>
              <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', color: 'var(--gray-900)' }}>
                <CheckCircle2 color="var(--success-500)" size={28} />
                User Created
              </h2>
              <button className="modal-close" onClick={() => setCredsModal(null)}><X size={24} /></button>
            </div>

            <div style={{ padding: '0 2rem' }}>
              <p style={{ marginBottom: '1.5rem', color: 'var(--gray-600)', lineHeight: '1.5', marginTop: 0 }}>
                Share these login credentials with the user. This is the <strong>only time</strong> the password will be shown.
              </p>

              <div style={{ background: 'var(--gray-50)', padding: '1.25rem', borderRadius: 'var(--radius-lg)', border: '1px solid var(--gray-200)', marginBottom: '1.5rem' }}>
                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label>Email Address</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input type="text" readOnly value={credsModal.email} className="form-input" style={{ flex: 1, backgroundColor: 'white' }} />
                    <button className="btn-icon" onClick={() => copyToClipboard(credsModal.email, 'email')} title="Copy Email">
                      {copiedField === 'email' ? <Check size={18} className="text-success-600" /> : <Copy size={18} />}
                    </button>
                  </div>
                </div>

                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Temporary Password</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input type="text" readOnly value={credsModal.password} className="form-input" style={{ flex: 1, fontFamily: 'monospace', backgroundColor: 'white' }} />
                    <button className="btn-icon" onClick={() => copyToClipboard(credsModal.password, 'password')} title="Copy Password">
                      {copiedField === 'password' ? <Check size={18} color="var(--success-600)" /> : <Copy size={18} />}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-actions" style={{ padding: '0 2rem 2rem', borderTop: 'none', margin: 0 }}>
              <button className="btn-primary" onClick={() => setCredsModal(null)} style={{ width: '100%' }}>Done</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default AdminDashboard;